import React from "react";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "../../lib/track";

/**
 * JournalClosingCard — editorial panel beneath each post that turns
 * the reflection into a next aligned step. Path is driven by the
 * post's `primaryPath` field (snapshot | book | blueprint |
 * consultation). Per the OSIRIS Gateway Architecture, the
 * "consultation" path now routes to the Clarity Reset Session gateway
 * ($149) — no more generic Book-a-Consultation forms in the journal.
 */
const COPY = {
  snapshot: {
    line: "If this found something in you, begin with the Snapshot.",
    cta: "Find Your Current Signal",
    href: "/frequency-snapshot",
  },
  "start-here": {
    line: "If you are ready to begin, the first move is named clearly on the Start Here page.",
    cta: "Start Here",
    href: "/start-here",
  },
  book: {
    line: "The book opens the full framework behind this.",
    cta: "Read Authentic Momentum",
    href: "/book",
  },
  blueprint: {
    line: "Your answers may already contain the structure. The Personal Action Plan helps organize them.",
    cta: "Start the Personal Action Plan",
    href: "/?checkout=blueprint",
  },
  consultation: {
    line: "Bring the question you have been circling.",
    cta: "Book the Clarity Reset Session · $149",
    href: null,
    action: "orientation_reset",
  },
  orientation_reset: {
    line: "Bring the question you have been circling.",
    cta: "Book the Clarity Reset Session · $149",
    href: null,
    action: "orientation_reset",
  },
};

const JournalClosingCard = ({ post }) => {
  const path = COPY[post.primaryPath] || COPY.snapshot;

  const handleClick = () => {
    trackEvent("journal_closing_cta_clicked", {
      slug: post.slug,
      primary_path: post.primaryPath,
    });
    if (path.action === "orientation_reset") {
      window.dispatchEvent(new CustomEvent("am:open-orientation-reset-checkout"));
    }
  };

  const inner = (
    <>
      <p className="font-serif italic text-themed text-lg md:text-xl leading-relaxed">
        {path.line}
      </p>
      {post.closingInvitation && (
        <p className="text-themed-muted text-sm mt-3 leading-relaxed">
          {post.closingInvitation}
        </p>
      )}
      <span className="btn-gold mt-7 inline-flex items-center gap-2 self-start">
        {path.cta} <ArrowRight size={13} />
      </span>
    </>
  );

  return (
    <aside
      data-testid={`journal-closing-card-${post.primaryPath}`}
      className="journal-closing relative my-16 p-8 md:p-10 flex flex-col"
    >
      <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
        A next aligned step
      </p>
      {path.href ? (
        <a
          href={path.href}
          onClick={handleClick}
          data-testid={`journal-closing-link-${post.primaryPath}`}
          className="contents"
        >
          {inner}
        </a>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          data-testid={`journal-closing-button-${post.primaryPath}`}
          className="text-left contents"
        >
          {inner}
        </button>
      )}
    </aside>
  );
};

export default JournalClosingCard;
